// Porta de reference/expo-frontend/lib/leaderboard.ts. Única adaptação: o
// web não sincroniza o estado completo de apostas, então o tournamentCount
// por usuário já chega pronto do servidor (GET /api/members) em vez de ser
// derivado de `bets` no cliente.
import { competitionRanks } from "@/lib/competitionRank";

export type LeaderboardEntry = {
  userId: string;
  name: string;
  nickname: string | null;
  hits: number;
  totalWon: number;
  tournamentCount: number;
};

export type Member = {
  id: string;
  name: string;
  nickname: string | null;
  tournamentCount: number;
};

export type SettlementSummary = {
  tournamentId: string;
  winnersCount: number;
  prizePerWinner: number;
  winnerUserIds: string[];
  winnerPayouts?:
    | { betId?: string; userId: string; stake?: number; prize: number }[]
    | null;
  /** IDs de jogadores CUT/WD/DQ persistidos na liquidação (server-side). */
  cutPlayerIds?: string[] | null;
  /**
   * Acertos por usuário (melhor aposta, 0-3), computados na liquidação —
   * permitem mostrar "quem acertou mais" mesmo quando ninguém ganhou.
   * Ausente em liquidações antigas.
   */
  hitStats?: { userId: string; hits: number }[] | null;
  /** Pote empurrado para o próximo torneio quando ninguém ganhou. */
  jackpotAdded?: number;
  settledAt: string;
};

export type LeaderboardSet = {
  byHits: LeaderboardEntry[];
  byEarnings: LeaderboardEntry[];
  byTournaments: LeaderboardEntry[];
  /** Tie-aware competition ranks aligned with byHits */
  ranksByHits: number[];
  /** Tie-aware competition ranks aligned with byEarnings */
  ranksByEarnings: number[];
  /** Tie-aware competition ranks aligned with byTournaments */
  ranksByTournaments: number[];
};

export type PositionDeltas = {
  hits: Map<string, number>;
  earnings: Map<string, number>;
  tournaments: Map<string, number>;
};

export function computeLeaderboards(
  members: Member[],
  settlements: SettlementSummary[],
): LeaderboardSet {
  const map = new Map<string, LeaderboardEntry>();
  for (const member of members) {
    map.set(member.id, {
      userId: member.id,
      name: member.name,
      nickname: member.nickname ?? null,
      hits: 0,
      totalWon: 0,
      tournamentCount: member.tournamentCount,
    });
  }

  for (const settlement of settlements) {
    if (settlement.winnerPayouts && settlement.winnerPayouts.length > 0) {
      for (const payout of settlement.winnerPayouts) {
        const entry = map.get(payout.userId);
        if (entry) {
          entry.hits += 1;
          entry.totalWon += payout.prize;
        }
      }
    } else {
      for (const winnerId of settlement.winnerUserIds) {
        const entry = map.get(winnerId);
        if (entry) {
          entry.hits += 1;
          entry.totalWon += settlement.prizePerWinner;
        }
      }
    }
  }

  const all = Array.from(map.values());

  const byHits = [...all].sort((a, b) => {
    if (b.hits !== a.hits) return b.hits - a.hits;
    return b.totalWon - a.totalWon;
  });
  const byEarnings = [...all].sort((a, b) => b.totalWon - a.totalWon);
  const byTournaments = [...all].sort((a, b) => {
    if (b.tournamentCount !== a.tournamentCount)
      return b.tournamentCount - a.tournamentCount;
    return b.hits - a.hits;
  });

  return {
    byHits,
    byEarnings,
    byTournaments,
    ranksByHits: competitionRanks(byHits, (e) => e.hits),
    ranksByEarnings: competitionRanks(byEarnings, (e) => e.totalWon),
    ranksByTournaments: competitionRanks(
      byTournaments,
      (e) => e.tournamentCount,
    ),
  };
}

function buildRankMap(
  list: LeaderboardEntry[],
  ranks: number[],
): Map<string, number> {
  const map = new Map<string, number>();
  for (let i = 0; i < list.length; i++) {
    map.set(list[i]!.userId, ranks[i]!);
  }
  return map;
}

export function computePositionDeltas(
  current: LeaderboardSet,
  previous: LeaderboardSet,
): PositionDeltas {
  const prevHits = buildRankMap(previous.byHits, previous.ranksByHits);
  const prevEarnings = buildRankMap(
    previous.byEarnings,
    previous.ranksByEarnings,
  );
  const prevTournaments = buildRankMap(
    previous.byTournaments,
    previous.ranksByTournaments,
  );

  const hits = new Map<string, number>();
  const earnings = new Map<string, number>();
  const tournaments = new Map<string, number>();

  for (let i = 0; i < current.byHits.length; i++) {
    const entry = current.byHits[i]!;
    const prev = prevHits.get(entry.userId);
    if (prev !== undefined) {
      hits.set(entry.userId, prev - current.ranksByHits[i]!);
    }
  }
  for (let i = 0; i < current.byEarnings.length; i++) {
    const entry = current.byEarnings[i]!;
    const prev = prevEarnings.get(entry.userId);
    if (prev !== undefined) {
      earnings.set(entry.userId, prev - current.ranksByEarnings[i]!);
    }
  }
  for (let i = 0; i < current.byTournaments.length; i++) {
    const entry = current.byTournaments[i]!;
    const prev = prevTournaments.get(entry.userId);
    if (prev !== undefined) {
      tournaments.set(entry.userId, prev - current.ranksByTournaments[i]!);
    }
  }

  return { hits, earnings, tournaments };
}
